/**
 * 地形 Mesh —— 從高度圖生成 3D 地形
 *
 * 渲染品質：
 * - TSL shader 從高度圖鄰近像素計算法線（512×512 精度）
 * - 坡度+高度+侵蝕感知著色（懸崖/草地/岩石/雪/沙灘）
 * - Triplanar micro-detail 紋理（近距離材質質感）
 * - 程序化水面：Gerstner 波 + 菲涅耳 + 折射 + 焦散
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  positionLocal,
  positionWorld,
  normalWorld,
  cameraPosition,
  texture,
  uniform,
  uv,
  smoothstep,
  mix,
  clamp,
  normalize,
  pow,
  fract,
  sin,
  cos,
  dot,
  floor,
  abs,
  max,
  min,
  time as tslTime,
} from 'three/tsl'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  WATER_HEIGHT_RATIO,
  WATER_LEVEL,
  HEIGHTMAP_SIZE,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

const SEGMENTS = 1024

export function TerrainMesh() {
  const meshRef = useRef<THREE.Mesh>(null)
  const lastHmapVersion = useRef(0)

  const { heightTex, material } = useMemo(() => {
    const tex = new THREE.DataTexture(
      terrainBuffer.heightmap,
      HEIGHTMAP_SIZE,
      HEIGHTMAP_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType,
    )
    tex.needsUpdate = true
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter

    const heightScale = uniform(TERRAIN_HEIGHT_SCALE)
    const texelSize = uniform(1.0 / HEIGHTMAP_SIZE)

    const mat = new THREE.MeshStandardNodeMaterial()
    mat.side = THREE.DoubleSide
    mat.flatShading = false
    mat.transparent = true
    mat.depthWrite = true

    // ── 頂點位移 ──
    mat.positionNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const texUV = uv()
      const sample = texture(tex, texUV)
      const h = sample.x.mul(heightScale)
      return vec3(localPos.x, localPos.y, h)
    })()

    // ── 從高度圖鄰近像素計算法線（比幾何法線精度更高） ──
    mat.normalNode = Fn(() => {
      const texUV = uv()
      const eps = texelSize

      // 取四鄰像素高度
      const hC = texture(tex, texUV).x
      const hR = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const hL = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const hU = texture(tex, texUV.add(vec2(float(0), eps))).x
      const hD = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x

      // 中央差分求斜率（世界空間）
      // PlaneGeometry 是 XY 平面旋轉 -90° 放置，所以 UV.x → world X, UV.y → world -Z
      // 在 local space 裡 z 軸是高度
      const worldStep = float(TERRAIN_WORLD_SIZE).div(float(HEIGHTMAP_SIZE))
      const dhdx = hR.sub(hL).mul(heightScale).div(worldStep.mul(float(2)))
      const dhdy = hU.sub(hD).mul(heightScale).div(worldStep.mul(float(2)))

      // local space normal: cross(tangentX, tangentY)
      // tangentX = (1, 0, dhdx), tangentY = (0, 1, dhdy)
      // normal = (-dhdx, -dhdy, 1)
      return normalize(vec3(dhdx.negate(), dhdy.negate(), float(1)))
    })()

    // ── TSL hash noise ──
    const hash21 = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h = dot(p, vec2(127.1, 311.7))
      return fract(sin(h).mul(float(43758.5453)))
    })

    const hash22 = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const hx = dot(p, vec2(127.1, 311.7))
      const hy = dot(p, vec2(269.5, 183.3))
      return vec2(fract(sin(hx).mul(float(43758.5453))), fract(sin(hy).mul(float(21345.6789))))
    })

    // 2D value noise（雙線性插值）
    const valueNoise = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const i = floor(p)
      const f = fract(p)
      const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
      const a = hash21(i)
      const b = hash21(i.add(vec2(1, 0)))
      const c = hash21(i.add(vec2(0, 1)))
      const d = hash21(i.add(vec2(1, 1)))
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
    })

    // Voronoi noise（沙地/岩石 micro-detail）
    const voronoiNoise = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const i = floor(p)
      const f = fract(p)
      const minDist = float(8.0).toVar()
      // 3×3 鄰域搜索
      const jx = float(-1).toVar()
      // 簡化為 4 鄰域近似 Voronoi
      const c0 = hash22(i)
      const d0 = f.sub(c0)
      minDist.assign(min(minDist, dot(d0, d0)))
      const c1 = hash22(i.add(vec2(1, 0)))
      const d1 = f.sub(c1).sub(vec2(1, 0))
      minDist.assign(min(minDist, dot(d1, d1)))
      const c2 = hash22(i.add(vec2(0, 1)))
      const d2 = f.sub(c2).sub(vec2(0, 1))
      minDist.assign(min(minDist, dot(d2, d2)))
      const c3 = hash22(i.add(vec2(1, 1)))
      const d3 = f.sub(c3).sub(vec2(1, 1))
      minDist.assign(min(minDist, dot(d3, d3)))
      return pow(minDist, float(0.5))
    })

    // FBM (Fractal Brownian Motion) — 多八度 noise 疊加
    const fbm = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const v = float(0).toVar()
      const a = float(0.5).toVar()
      const shift = vec2(100.0, 100.0)
      v.addAssign(a.mul(valueNoise(p)))
      p.assign(p.mul(float(2.0)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(valueNoise(p)))
      p.assign(p.mul(float(2.0)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(valueNoise(p)))
      return v
    })

    // ── 坡度+高度+侵蝕+noise 著色 ──
    mat.colorNode = Fn(() => {
      const texUV = uv()
      const eps = texelSize
      const sample = texture(tex, texUV)
      const normH = sample.x // 0-1 normalized height

      // 坡度計算
      const hR = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const hL = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const hU = texture(tex, texUV.add(vec2(float(0), eps))).x
      const hD = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x

      const worldStep = float(TERRAIN_WORLD_SIZE).div(float(HEIGHTMAP_SIZE))
      const dhdx = hR.sub(hL).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const dhdy = hU.sub(hD).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const slopeLen = pow(dhdx.mul(dhdx).add(dhdy.mul(dhdy)), float(0.5))
      const normalY = float(1).div(pow(float(1).add(slopeLen.mul(slopeLen)), float(0.5)))

      // ── noise breakup（打破色帶，對標原版 Buffer C） ──
      const noiseCoord = texUV.mul(float(HEIGHTMAP_SIZE))
      const breakup = valueNoise(noiseCoord.mul(float(0.5)))
        .mul(float(0.6))
        .add(valueNoise(noiseCoord.mul(float(1.2))).mul(float(0.3)))
        .add(valueNoise(noiseCoord.mul(float(3.0))).mul(float(0.1)))
      // breakup 範圍 0~1，用於偏移色帶邊界
      const breakOffset = breakup.sub(float(0.5)).mul(float(0.015))

      // ── 顏色定義（完全對標原版 terrain-erosion common.glsl） ──
      const waterH = float(WATER_HEIGHT_RATIO)
      const grassH = float(0.47)

      const sand = vec3(0.80, 0.70, 0.60)
      const grass1 = vec3(0.15, 0.30, 0.10)
      const grass2 = vec3(0.40, 0.50, 0.20)
      const treeColor = vec3(0.12, 0.26, 0.10)
      const dirt = vec3(0.60, 0.50, 0.40)
      const cliff = vec3(0.22, 0.20, 0.20)
      const snow = vec3(1.0, 1.0, 1.0)

      // 加 noise 偏移的高度（打破清晰的等高線色帶）
      const nH = normH.add(breakOffset)

      // 水下到沙灘
      let color = mix(vec3(0.05, 0.12, 0.18), sand, smoothstep(waterH.sub(float(0.01)), waterH.add(float(0.005)), nH))
      // 沙灘到草地
      color = mix(color, grass1, smoothstep(waterH.add(float(0.005)), grassH, nH))
      // 草地雙色混合
      color = mix(color, grass2, smoothstep(grassH, grassH.add(float(0.02)), nH))
      // 植被/樹木覆蓋（平坦處、中等高度）
      const treeCoverage = smoothstep(float(0.96), float(1.0), normalY)
        .mul(smoothstep(grassH.add(float(0.01)), grassH.add(float(0.05)), normH))
        .mul(float(1).sub(smoothstep(float(0.50), float(0.55), normH)))
      color = mix(color, treeColor, treeCoverage.mul(float(0.6)).mul(breakup))
      // 草地到泥土（noise 打破邊界）
      color = mix(color, dirt, smoothstep(float(0.50), float(0.55), nH))
      // 泥土到岩石
      color = mix(color, cliff, smoothstep(float(0.55), float(0.65), nH))
      // 雪線（noise 打破使雪線自然不規則）
      const snowThreshold = float(0.75).add(breakup.sub(float(0.5)).mul(float(0.08)))
      color = mix(color, snow, smoothstep(snowThreshold, snowThreshold.add(float(0.07)), normH))

      // ── 坡度影響：陡峭處顯示懸崖色 + 中等坡度顯示泥土 ──
      const cliffBlend = float(1).sub(smoothstep(float(0.60), float(0.85), normalY))
      const dirtBlend = float(1).sub(smoothstep(float(0.85), float(0.95), normalY)).mul(float(1).sub(cliffBlend))
      const aboveWater = smoothstep(waterH, waterH.add(float(0.01)), normH)
      color = mix(color, dirt, dirtBlend.mul(aboveWater).mul(float(0.5)))
      color = mix(color, cliff, cliffBlend.mul(aboveWater))

      // ── 侵蝕 AO（拉普拉斯估算局部凹凸） ──
      const hC = sample.x
      const laplacian = hR.add(hL).add(hU).add(hD).sub(hC.mul(float(4)))
      const aoFactor = clamp(float(1).sub(laplacian.mul(float(12))), float(0.55), float(1.2))
      color = mix(color, color.mul(aoFactor), aboveWater)

      // ── 細微 noise 色彩變化（打破大面積平塗感） ──
      const microNoise = valueNoise(noiseCoord.mul(float(6.0))).sub(float(0.5)).mul(float(0.04))
      color = color.add(vec3(microNoise, microNoise, microNoise).mul(aboveWater))

      // ── Triplanar Micro-Detail（近距離材質質感） ──
      // 根據生態帶混合不同的高頻紋理
      const worldCoord = texUV.mul(float(TERRAIN_WORLD_SIZE))

      // 沙地：細粒 Voronoi
      const sandDetail = voronoiNoise(worldCoord.mul(float(2.5)))
        .sub(float(0.5)).mul(float(0.06))
      const sandMask = smoothstep(waterH.sub(float(0.005)), waterH.add(float(0.01)), normH)
        .mul(float(1).sub(smoothstep(grassH.sub(float(0.01)), grassH, normH)))

      // 岩石/崖面：FBM 裂紋
      const rockDetail = fbm(worldCoord.mul(float(1.5)))
        .sub(float(0.5)).mul(float(0.08))
      const rockMask = cliffBlend.mul(aboveWater)

      // 草地：微變化
      const grassDetail = valueNoise(worldCoord.mul(float(4.0)))
        .sub(float(0.5)).mul(float(0.035))
      const grassMask = smoothstep(grassH, grassH.add(float(0.03)), normH)
        .mul(float(1).sub(smoothstep(float(0.50), float(0.55), normH)))
        .mul(smoothstep(float(0.85), float(0.95), normalY))

      // 雪地：微結晶
      const snowDetail = valueNoise(worldCoord.mul(float(3.0)))
        .mul(float(0.04))
      const snowMask = smoothstep(snowThreshold, snowThreshold.add(float(0.07)), normH)

      // 混合 micro-detail
      const detail = float(0).toVar()
      detail.addAssign(sandDetail.mul(sandMask))
      detail.addAssign(rockDetail.mul(rockMask))
      detail.addAssign(grassDetail.mul(grassMask))
      detail.addAssign(snowDetail.mul(snowMask))

      color = color.add(vec3(detail, detail, detail).mul(aboveWater))

      return vec4(color, float(1.0))
    })()

    // ── 水面以下漸變透明（消除可見邊框） ──
    mat.opacityNode = Fn(() => {
      const texUV = uv()
      const normH = texture(tex, texUV).x
      const waterH = float(WATER_HEIGHT_RATIO)
      // 水面以上完全不透明，水面以下快速透明
      return smoothstep(waterH.sub(float(0.015)), waterH.add(float(0.003)), normH)
    })()

    mat.roughnessNode = float(0.85)
    mat.metalnessNode = float(0.0)

    return { heightTex: tex, material: mat }
  }, [])

  // 每幀檢查 terrainBuffer 版本，就地更新紋理
  useFrame(() => {
    if (terrainBuffer.heightmapVersion !== lastHmapVersion.current) {
      lastHmapVersion.current = terrainBuffer.heightmapVersion
      heightTex.image = {
        data: terrainBuffer.heightmap,
        width: HEIGHTMAP_SIZE,
        height: HEIGHTMAP_SIZE,
      }
      heightTex.needsUpdate = true
    }
  })

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE, TERRAIN_WORLD_SIZE, SEGMENTS, SEGMENTS]} />
      <primitive object={material} />
    </mesh>
  )
}

/** 高品質程序化水面 —— Gerstner 波 + 深度感知 + 菲涅耳 + 岸邊泡沫 + 太陽鏡面反射 */
export function WaterPlane() {
  const meshRef = useRef<THREE.Mesh>(null)
  const lastWaterHmapVersion = useRef(0)

  const { heightTex, material } = useMemo(() => {
    // 地形高度圖（用於計算水深）
    const tex = new THREE.DataTexture(
      terrainBuffer.heightmap,
      HEIGHTMAP_SIZE,
      HEIGHTMAP_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType,
    )
    tex.needsUpdate = true
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter

    const mat = new THREE.MeshStandardNodeMaterial()
    mat.transparent = true
    mat.side = THREE.DoubleSide
    mat.depthWrite = false

    const time = tslTime
    const waterLevelU = uniform(WATER_LEVEL)
    const heightScaleU = uniform(TERRAIN_HEIGHT_SCALE)
    const worldSizeU = uniform(TERRAIN_WORLD_SIZE)

    // ── 雜湊與噪聲 ──
    const waterHash = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h = dot(p, vec2(127.1, 311.7))
      return fract(sin(h).mul(float(43758.5453)))
    })

    const waterNoise = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const i = floor(p)
      const f = fract(p)
      const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
      const a = waterHash(i)
      const b = waterHash(i.add(vec2(1, 0)))
      const c = waterHash(i.add(vec2(0, 1)))
      const d = waterHash(i.add(vec2(1, 1)))
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
    })

    // ── Gerstner 波 ──
    const gerstnerWave = Fn(([pos_immutable, dir_immutable, amplitude_immutable, freq_immutable, speed_immutable]: [any, any, any, any, any]) => {
      const pos = vec2(pos_immutable).toVar()
      const dir = vec2(dir_immutable).toVar()
      const amp = float(amplitude_immutable)
      const frq = float(freq_immutable)
      const spd = float(speed_immutable)
      const phase = dot(dir, pos).mul(frq).add(time.mul(spd))
      const s = sin(phase)
      const c = cos(phase)
      const steepness = float(0.5)
      const dx = steepness.mul(amp).mul(dir.x).mul(c)
      const dz = steepness.mul(amp).mul(dir.y).mul(c)
      const dy = amp.mul(s)
      return vec3(dx, dy, dz)
    })

    // 4 層波浪疊加高度
    const sumWaveHeight = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h1 = gerstnerWave(p, vec2(0.7, 0.3), float(1.2), float(0.04), float(0.4)).y
      const h2 = gerstnerWave(p, vec2(-0.4, 0.6), float(0.7), float(0.09), float(0.65)).y
      const h3 = gerstnerWave(p, vec2(0.3, -0.8), float(0.3), float(0.2), float(1.0)).y
      const h4 = gerstnerWave(p, vec2(-0.6, -0.4), float(0.12), float(0.45), float(1.5)).y
      return h1.add(h2).add(h3).add(h4)
    })

    // ── 頂點位移（4 層 Gerstner 波） ──
    mat.positionNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const worldXZ = vec2(localPos.x, localPos.y)

      const w1 = gerstnerWave(worldXZ, vec2(0.7, 0.3), float(1.2), float(0.04), float(0.4))
      const w2 = gerstnerWave(worldXZ, vec2(-0.4, 0.6), float(0.7), float(0.09), float(0.65))
      const w3 = gerstnerWave(worldXZ, vec2(0.3, -0.8), float(0.3), float(0.2), float(1.0))
      const w4 = gerstnerWave(worldXZ, vec2(-0.6, -0.4), float(0.12), float(0.45), float(1.5))

      const d = w1.add(w2).add(w3).add(w4)
      return vec3(localPos.x.add(d.x), localPos.y.add(d.z), d.y)
    })()

    // ── 法線（有限差分） ──
    mat.normalNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const worldXZ = vec2(localPos.x, localPos.y)
      const eps = float(0.5)

      const hC = sumWaveHeight(worldXZ)
      const hR = sumWaveHeight(worldXZ.add(vec2(eps, float(0))))
      const hU = sumWaveHeight(worldXZ.add(vec2(float(0), eps)))

      const dhdx = hR.sub(hC).div(eps)
      const dhdy = hU.sub(hC).div(eps)
      return normalize(vec3(dhdx.negate(), dhdy.negate(), float(1)))
    })()

    // ── 計算水深（地形高度圖採樣） ──
    // 地形 mesh 水面以下已透明，所以水面下方看不到地形
    // 深度僅用於色調漸變（近岸淺色 → 深海深色）
    const computeDepth = Fn(([lx, ly]: [any, any]) => {
      const terrainUV = vec2(
        float(lx).div(worldSizeU).add(float(0.5)),
        float(ly).div(worldSizeU).add(float(0.5)),
      )
      // 是否在可編輯區域內
      const insideX = smoothstep(float(0.5), float(0.45), abs(terrainUV.x.sub(float(0.5))))
      const insideZ = smoothstep(float(0.5), float(0.45), abs(terrainUV.y.sub(float(0.5))))
      const inside = insideX.mul(insideZ)

      const terrainH = texture(tex, clamp(terrainUV, float(0), float(1))).x.mul(heightScaleU)
      const insideDepth = max(float(0), waterLevelU.sub(terrainH))

      // 界外一律深海
      return mix(float(60), insideDepth, inside)
    })

    // ── 片段著色：乾淨水色 + 菲涅耳天空反射 + 太陽高光 ──
    mat.colorNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const depth = computeDepth(localPos.x, localPos.y)

      // 兩段式水色（淺水綠 → 深海藍）
      const waterShallow = vec3(0.08, 0.35, 0.32)
      const waterDeep = vec3(0.015, 0.06, 0.14)
      const baseColor = mix(waterShallow, waterDeep, smoothstep(float(0), float(40), depth))

      // 菲涅耳——掠射角反射天空
      const viewDir = normalize(cameraPosition.sub(positionWorld))
      const N = normalWorld
      const NdotV = max(float(0.001), dot(viewDir, N))
      const fresnel = pow(float(1).sub(NdotV), float(4))

      // 反射方向 → 假天空色
      const I = viewDir.negate()
      const reflectDir = I.sub(N.mul(dot(N, I).mul(float(2))))
      const skyReflect = mix(
        vec3(0.6, 0.72, 0.82),
        vec3(0.32, 0.52, 0.82),
        max(float(0), reflectDir.y),
      )

      // 太陽鏡面反射
      const sunDir = normalize(vec3(0.5, 0.7, 0.3))
      const sunSpec = pow(max(float(0), dot(reflectDir, sunDir)), float(512))
      const sunHighlight = vec3(1.0, 0.95, 0.85).mul(sunSpec).mul(float(2))

      const waveH = sumWaveHeight(vec2(localPos.x, localPos.y))
      const shore = float(1).sub(smoothstep(float(0.6), float(8), depth))
      const foam = clamp(shore.mul(float(0.75)).add(smoothstep(float(0.4), float(1.1), waveH).mul(shore)), float(0), float(1))
      const withFoam = mix(mix(baseColor, skyReflect, fresnel.mul(float(0.5))), vec3(0.86, 0.9, 0.88), foam.mul(float(0.65)))
      const finalColor = withFoam.add(sunHighlight.mul(float(1).sub(foam)))
      return vec4(finalColor, float(1.0))
    })()

    // ── 透明度：深處不透明，淺處微透 ──
    mat.opacityNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const depth = computeDepth(localPos.x, localPos.y)

      const viewDir = normalize(cameraPosition.sub(positionWorld))
      const NdotV = max(float(0.001), dot(viewDir, normalWorld))
      const fresnel = pow(float(1).sub(NdotV), float(3))

      const depthAlpha = smoothstep(float(0), float(12), depth).mul(float(0.7)).add(float(0.2))
      return min(float(0.92), depthAlpha.add(fresnel.mul(float(0.2))))
    })()

    mat.roughnessNode = float(0.02)
    mat.metalnessNode = float(0.0)

    return { heightTex: tex, material: mat }
  }, [])

  // 同步地形高度圖（水面需要知道最新地形來計算深度）
  useFrame(() => {
    if (terrainBuffer.heightmapVersion !== lastWaterHmapVersion.current) {
      lastWaterHmapVersion.current = terrainBuffer.heightmapVersion
      heightTex.image = {
        data: terrainBuffer.heightmap,
        width: HEIGHTMAP_SIZE,
        height: HEIGHTMAP_SIZE,
      }
      heightTex.needsUpdate = true
    }
  })

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, WATER_LEVEL, 0]}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE * 5, TERRAIN_WORLD_SIZE * 5, 192, 192]} />
      <primitive object={material} />
    </mesh>
  )
}

export { TERRAIN_WORLD_SIZE, TERRAIN_HEIGHT_SCALE, WATER_HEIGHT_RATIO }
